/**
 * 多语言支持系统
 * Internationalization (i18n) System
 */

export type Language =
  | "zh_cn"  // 简体中文
  | "en"     // English
  | "zh_tw"  // 繁體中文
  | "ja"     // 日本語
  | "ar"     // العربية
  | "ko"     // 한국어
  | "de"     // Deutsch
  | "fr"     // Français
  | "ru"     // Русский
  | "es"     // Español
  | "pt"     // Português
  | "th"     // ไทย
  | "indo"   // Indonesia
  | "it"     // Italiano
  | "vi"     // Tiếng Việt
  | "tr";    // Türkçe

export interface LanguageInfo {
  code: Language;
  name: string;
  nativeName: string;
}

export const SUPPORTED_LANGUAGES: LanguageInfo[] = [
  { code: "zh_cn", name: "Simplified Chinese", nativeName: "简体中文" },
  { code: "en", name: "English", nativeName: "English" },
  { code: "zh_tw", name: "Traditional Chinese", nativeName: "繁體中文" },
  { code: "ja", name: "Japanese", nativeName: "日本語" },
  { code: "ar", name: "Arabic", nativeName: "العربية" },
  { code: "ko", name: "Korean", nativeName: "한국어" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "ru", name: "Russian", nativeName: "Русский" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "pt", name: "Portuguese", nativeName: "Português" },
  { code: "th", name: "Thai", nativeName: "ไทย" },
  { code: "indo", name: "Indonesian", nativeName: "Bahasa Indonesia" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "vi", name: "Vietnamese", nativeName: "Tiếng Việt" },
  { code: "tr", name: "Turkish", nativeName: "Türkçe" }
];

export interface Translations {
  // 聊天界面
  chat: {
    title: string;
    placeholder: string;
    send: string;
    typing: string;
    clearChat: string;
    confirmClear: string;
    emoji: string;
  };
  // 设置页面
  settings: {
    title: string;
    subtitle: string;
    language: string;
    languageDesc: string;
    wallpaper: string;
    wallpaperDesc: string;
    charm: string;
    charmDesc: string;
    upload: string;
    uploadHint: string;
    preview: string;
    reset: string;
    save: string;
    backToChat: string;
    saved: string;
  };
  // 通用
  common: {
    confirm: string;
    cancel: string;
    success: string;
    error: string;
    loading: string;
  };
}

const translations: Record<Language, Translations> = {
  zh_cn: {
    chat: {
      title: "📱 小手机聊天",
      placeholder: "输入消息...",
      send: "发送",
      typing: "正在输入...",
      clearChat: "清空聊天",
      confirmClear: "确定要清空所有聊天记录吗？",
      emoji: "表情"
    },
    settings: {
      title: "📱 小手机设置",
      subtitle: "自定义你的小手机外观",
      language: "语言设置",
      languageDesc: "选择你的首选语言",
      wallpaper: "手机壁纸",
      wallpaperDesc: "上传自定义壁纸 (建议 360x800 像素)",
      charm: "手机挂件",
      charmDesc: "上传可爱挂件 (建议 170x170 像素，PNG 透明背景)",
      upload: "点击上传",
      uploadHint: "或拖拽文件到此处",
      preview: "预览",
      reset: "恢复默认",
      save: "保存设置",
      backToChat: "返回聊天",
      saved: "设置已保存！"
    },
    common: {
      confirm: "确定",
      cancel: "取消",
      success: "成功",
      error: "错误",
      loading: "加载中..."
    }
  },
  en: {
    chat: {
      title: "📱 Mini Phone Chat",
      placeholder: "Type a message...",
      send: "Send",
      typing: "Typing...",
      clearChat: "Clear Chat",
      confirmClear: "Are you sure you want to clear all chat history?",
      emoji: "Emoji"
    },
    settings: {
      title: "📱 Mini Phone Settings",
      subtitle: "Customize your mini phone appearance",
      language: "Language Settings",
      languageDesc: "Choose your preferred language",
      wallpaper: "Wallpaper",
      wallpaperDesc: "Upload custom wallpaper (Recommended: 360x800 pixels)",
      charm: "Phone Charm",
      charmDesc: "Upload cute charm (Recommended: 170x170 pixels, PNG with transparency)",
      upload: "Click to Upload",
      uploadHint: "or drag and drop files here",
      preview: "Preview",
      reset: "Reset to Default",
      save: "Save Settings",
      backToChat: "Back to Chat",
      saved: "Settings saved!"
    },
    common: {
      confirm: "Confirm",
      cancel: "Cancel",
      success: "Success",
      error: "Error",
      loading: "Loading..."
    }
  },
  zh_tw: {
    chat: {
      title: "📱 小手機聊天",
      placeholder: "輸入訊息...",
      send: "發送",
      typing: "正在輸入...",
      clearChat: "清空聊天",
      confirmClear: "確定要清空所有聊天記錄嗎？",
      emoji: "表情"
    },
    settings: {
      title: "📱 小手機設置",
      subtitle: "自定義你的小手機外觀",
      language: "語言設置",
      languageDesc: "選擇你的首選語言",
      wallpaper: "手機壁紙",
      wallpaperDesc: "上傳自定義壁紙（建議 360x800 像素）",
      charm: "手機掛件",
      charmDesc: "上傳可愛掛件（建議 170x170 像素，PNG 透明背景）",
      upload: "點擊上傳",
      uploadHint: "或拖曳文件到此處",
      preview: "預覽",
      reset: "恢復默認",
      save: "保存設置",
      backToChat: "返回聊天",
      saved: "設置已保存！"
    },
    common: {
      confirm: "確定",
      cancel: "取消",
      success: "成功",
      error: "錯誤",
      loading: "加載中..."
    }
  },
  ja: {
    chat: {
      title: "📱 ミニフォンチャット",
      placeholder: "メッセージを入力...",
      send: "送信",
      typing: "入力中...",
      clearChat: "チャットをクリア",
      confirmClear: "すべてのチャット履歴を削除してもよろしいですか？",
      emoji: "絵文字"
    },
    settings: {
      title: "📱 ミニフォン設定",
      subtitle: "ミニフォンの外観をカスタマイズ",
      language: "言語設定",
      languageDesc: "希望の言語を選択",
      wallpaper: "壁紙",
      wallpaperDesc: "カスタム壁紙をアップロード（推奨：360x800ピクセル）",
      charm: "チャーム",
      charmDesc: "かわいいチャームをアップロード（推奨：170x170ピクセル、PNG透過）",
      upload: "クリックしてアップロード",
      uploadHint: "またはファイルをドラッグ＆ドロップ",
      preview: "プレビュー",
      reset: "デフォルトに戻す",
      save: "設定を保存",
      backToChat: "チャットに戻る",
      saved: "設定が保存されました！"
    },
    common: {
      confirm: "確認",
      cancel: "キャンセル",
      success: "成功",
      error: "エラー",
      loading: "読み込み中..."
    }
  },
  ar: {
    chat: {
      title: "📱 دردشة الهاتف الصغير",
      placeholder: "...اكتب رسالة",
      send: "إرسال",
      typing: "...جاري الكتابة",
      clearChat: "مسح الدردشة",
      confirmClear: "هل أنت متأكد من مسح جميع سجلات الدردشة؟",
      emoji: "رموز تعبيرية"
    },
    settings: {
      title: "📱 إعدادات الهاتف الصغير",
      subtitle: "تخصيص مظهر الهاتف الصغير",
      language: "إعدادات اللغة",
      languageDesc: "اختر لغتك المفضلة",
      wallpaper: "الخلفية",
      wallpaperDesc: "بكسل 360x800 :تحميل خلفية مخصصة (مستحسن",
      charm: "سحر الهاتف",
      charmDesc: "بكسل، شفافية PNG 170x170 :تحميل سحر لطيف (مستحسن",
      upload: "انقر للتحميل",
      uploadHint: "أو اسحب الملفات وأفلتها هنا",
      preview: "معاينة",
      reset: "إعادة تعيين إلى الافتراضي",
      save: "حفظ الإعدادات",
      backToChat: "العودة إلى الدردشة",
      saved: "!تم حفظ الإعدادات"
    },
    common: {
      confirm: "تأكيد",
      cancel: "إلغاء",
      success: "نجاح",
      error: "خطأ",
      loading: "...جاري التحميل"
    }
  },
  ko: {
    chat: {
      title: "📱 미니 폰 채팅",
      placeholder: "메시지 입력...",
      send: "전송",
      typing: "입력 중...",
      clearChat: "채팅 지우기",
      confirmClear: "모든 채팅 기록을 삭제하시겠습니까?",
      emoji: "이모지"
    },
    settings: {
      title: "📱 미니 폰 설정",
      subtitle: "미니 폰 모양 사용자 정의",
      language: "언어 설정",
      languageDesc: "선호하는 언어 선택",
      wallpaper: "배경화면",
      wallpaperDesc: "사용자 정의 배경화면 업로드 (권장: 360x800 픽셀)",
      charm: "폰 참",
      charmDesc: "귀여운 참 업로드 (권장: 170x170 픽셀, PNG 투명)",
      upload: "클릭하여 업로드",
      uploadHint: "또는 파일을 여기에 드래그 앤 드롭",
      preview: "미리보기",
      reset: "기본값으로 재설정",
      save: "설정 저장",
      backToChat: "채팅으로 돌아가기",
      saved: "설정이 저장되었습니다!"
    },
    common: {
      confirm: "확인",
      cancel: "취소",
      success: "성공",
      error: "오류",
      loading: "로딩 중..."
    }
  },
  de: {
    chat: {
      title: "📱 Mini-Telefon-Chat",
      placeholder: "Nachricht eingeben...",
      send: "Senden",
      typing: "Tippt...",
      clearChat: "Chat löschen",
      confirmClear: "Möchten Sie wirklich alle Chatnachrichten löschen?",
      emoji: "Emoji"
    },
    settings: {
      title: "📱 Mini-Telefon-Einstellungen",
      subtitle: "Passen Sie das Aussehen Ihres Mini-Telefons an",
      language: "Spracheinstellungen",
      languageDesc: "Wählen Sie Ihre bevorzugte Sprache",
      wallpaper: "Hintergrundbild",
      wallpaperDesc: "Benutzerdefiniertes Hintergrundbild hochladen (Empfohlen: 360x800 Pixel)",
      charm: "Telefon-Anhänger",
      charmDesc: "Süßen Anhänger hochladen (Empfohlen: 170x170 Pixel, PNG mit Transparenz)",
      upload: "Zum Hochladen klicken",
      uploadHint: "oder Dateien hierher ziehen",
      preview: "Vorschau",
      reset: "Auf Standard zurücksetzen",
      save: "Einstellungen speichern",
      backToChat: "Zurück zum Chat",
      saved: "Einstellungen gespeichert!"
    },
    common: {
      confirm: "Bestätigen",
      cancel: "Abbrechen",
      success: "Erfolg",
      error: "Fehler",
      loading: "Laden..."
    }
  },
  fr: {
    chat: {
      title: "📱 Chat Mini Téléphone",
      placeholder: "Tapez un message...",
      send: "Envoyer",
      typing: "Tape...",
      clearChat: "Effacer le chat",
      confirmClear: "Êtes-vous sûr de vouloir effacer tout l'historique du chat?",
      emoji: "Emoji"
    },
    settings: {
      title: "📱 Paramètres Mini Téléphone",
      subtitle: "Personnalisez l'apparence de votre mini téléphone",
      language: "Paramètres de langue",
      languageDesc: "Choisissez votre langue préférée",
      wallpaper: "Fond d'écran",
      wallpaperDesc: "Télécharger un fond d'écran personnalisé (Recommandé: 360x800 pixels)",
      charm: "Charm de téléphone",
      charmDesc: "Télécharger un charm mignon (Recommandé: 170x170 pixels, PNG avec transparence)",
      upload: "Cliquez pour télécharger",
      uploadHint: "ou glissez-déposez les fichiers ici",
      preview: "Aperçu",
      reset: "Réinitialiser par défaut",
      save: "Enregistrer les paramètres",
      backToChat: "Retour au chat",
      saved: "Paramètres enregistrés!"
    },
    common: {
      confirm: "Confirmer",
      cancel: "Annuler",
      success: "Succès",
      error: "Erreur",
      loading: "Chargement..."
    }
  },
  ru: {
    chat: {
      title: "📱 Мини-телефон Чат",
      placeholder: "Введите сообщение...",
      send: "Отправить",
      typing: "Печатает...",
      clearChat: "Очистить чат",
      confirmClear: "Вы уверены, что хотите очистить всю историю чата?",
      emoji: "Эмодзи"
    },
    settings: {
      title: "📱 Настройки Мини-телефона",
      subtitle: "Настройте внешний вид вашего мини-телефона",
      language: "Настройки языка",
      languageDesc: "Выберите предпочитаемый язык",
      wallpaper: "Обои",
      wallpaperDesc: "Загрузить пользовательские обои (Рекомендуется: 360x800 пикселей)",
      charm: "Брелок телефона",
      charmDesc: "Загрузить милый брелок (Рекомендуется: 170x170 пикселей, PNG с прозрачностью)",
      upload: "Нажмите для загрузки",
      uploadHint: "или перетащите файлы сюда",
      preview: "Предпросмотр",
      reset: "Сбросить по умолчанию",
      save: "Сохранить настройки",
      backToChat: "Вернуться в чат",
      saved: "Настройки сохранены!"
    },
    common: {
      confirm: "Подтвердить",
      cancel: "Отмена",
      success: "Успех",
      error: "Ошибка",
      loading: "Загрузка..."
    }
  },
  es: {
    chat: {
      title: "📱 Chat Mini Teléfono",
      placeholder: "Escribe un mensaje...",
      send: "Enviar",
      typing: "Escribiendo...",
      clearChat: "Borrar chat",
      confirmClear: "¿Estás seguro de que quieres borrar todo el historial de chat?",
      emoji: "Emoji"
    },
    settings: {
      title: "📱 Configuración Mini Teléfono",
      subtitle: "Personaliza la apariencia de tu mini teléfono",
      language: "Configuración de idioma",
      languageDesc: "Elige tu idioma preferido",
      wallpaper: "Fondo de pantalla",
      wallpaperDesc: "Subir fondo de pantalla personalizado (Recomendado: 360x800 píxeles)",
      charm: "Charm de teléfono",
      charmDesc: "Subir charm lindo (Recomendado: 170x170 píxeles, PNG con transparencia)",
      upload: "Haz clic para subir",
      uploadHint: "o arrastra y suelta archivos aquí",
      preview: "Vista previa",
      reset: "Restablecer predeterminado",
      save: "Guardar configuración",
      backToChat: "Volver al chat",
      saved: "¡Configuración guardada!"
    },
    common: {
      confirm: "Confirmar",
      cancel: "Cancelar",
      success: "Éxito",
      error: "Error",
      loading: "Cargando..."
    }
  },
  pt: {
    chat: {
      title: "📱 Chat Mini Telefone",
      placeholder: "Digite uma mensagem...",
      send: "Enviar",
      typing: "Digitando...",
      clearChat: "Limpar chat",
      confirmClear: "Tem certeza de que deseja limpar todo o histórico de chat?",
      emoji: "Emoji"
    },
    settings: {
      title: "📱 Configurações Mini Telefone",
      subtitle: "Personalize a aparência do seu mini telefone",
      language: "Configurações de idioma",
      languageDesc: "Escolha seu idioma preferido",
      wallpaper: "Papel de parede",
      wallpaperDesc: "Enviar papel de parede personalizado (Recomendado: 360x800 pixels)",
      charm: "Charm de telefone",
      charmDesc: "Enviar charm fofo (Recomendado: 170x170 pixels, PNG com transparência)",
      upload: "Clique para enviar",
      uploadHint: "ou arraste e solte arquivos aqui",
      preview: "Visualizar",
      reset: "Redefinir para padrão",
      save: "Salvar configurações",
      backToChat: "Voltar ao chat",
      saved: "Configurações salvas!"
    },
    common: {
      confirm: "Confirmar",
      cancel: "Cancelar",
      success: "Sucesso",
      error: "Erro",
      loading: "Carregando..."
    }
  },
  th: {
    chat: {
      title: "📱 แชทโทรศัพท์มินิ",
      placeholder: "พิมพ์ข้อความ...",
      send: "ส่ง",
      typing: "กำลังพิมพ์...",
      clearChat: "ล้างแชท",
      confirmClear: "คุณแน่ใจหรือไม่ว่าต้องการล้างประวัติการแชททั้งหมด?",
      emoji: "อีโมจิ"
    },
    settings: {
      title: "📱 การตั้งค่าโทรศัพท์มินิ",
      subtitle: "ปรับแต่งรูปลักษณ์โทรศัพท์มินิของคุณ",
      language: "การตั้งค่าภาษา",
      languageDesc: "เลือกภาษาที่คุณต้องการ",
      wallpaper: "วอลเปเปอร์",
      wallpaperDesc: "อัปโหลดวอลเปเปอร์ที่กำหนดเอง (แนะนำ: 360x800 พิกเซล)",
      charm: "จี้โทรศัพท์",
      charmDesc: "อัปโหลดจี้น่ารัก (แนะนำ: 170x170 พิกเซล, PNG โปร่งใส)",
      upload: "คลิกเพื่ออัปโหลด",
      uploadHint: "หรือลากและวางไฟล์ที่นี่",
      preview: "ดูตัวอย่าง",
      reset: "รีเซ็ตเป็นค่าเริ่มต้น",
      save: "บันทึกการตั้งค่า",
      backToChat: "กลับไปแชท",
      saved: "บันทึกการตั้งค่าแล้ว!"
    },
    common: {
      confirm: "ยืนยัน",
      cancel: "ยกเลิก",
      success: "สำเร็จ",
      error: "ข้อผิดพลาด",
      loading: "กำลังโหลด..."
    }
  },
  indo: {
    chat: {
      title: "📱 Obrolan Telepon Mini",
      placeholder: "Ketik pesan...",
      send: "Kirim",
      typing: "Mengetik...",
      clearChat: "Hapus obrolan",
      confirmClear: "Apakah Anda yakin ingin menghapus semua riwayat obrolan?",
      emoji: "Emoji"
    },
    settings: {
      title: "📱 Pengaturan Telepon Mini",
      subtitle: "Sesuaikan tampilan telepon mini Anda",
      language: "Pengaturan Bahasa",
      languageDesc: "Pilih bahasa pilihan Anda",
      wallpaper: "Wallpaper",
      wallpaperDesc: "Unggah wallpaper kustom (Disarankan: 360x800 piksel)",
      charm: "Gantungan Telepon",
      charmDesc: "Unggah gantungan lucu (Disarankan: 170x170 piksel, PNG transparan)",
      upload: "Klik untuk mengunggah",
      uploadHint: "atau seret dan lepas file di sini",
      preview: "Pratinjau",
      reset: "Atur ulang ke default",
      save: "Simpan pengaturan",
      backToChat: "Kembali ke obrolan",
      saved: "Pengaturan disimpan!"
    },
    common: {
      confirm: "Konfirmasi",
      cancel: "Batal",
      success: "Berhasil",
      error: "Kesalahan",
      loading: "Memuat..."
    }
  },
  it: {
    chat: {
      title: "📱 Chat Mini Telefono",
      placeholder: "Digita un messaggio...",
      send: "Invia",
      typing: "Sta scrivendo...",
      clearChat: "Cancella chat",
      confirmClear: "Sei sicuro di voler cancellare tutta la cronologia della chat?",
      emoji: "Emoji"
    },
    settings: {
      title: "📱 Impostazioni Mini Telefono",
      subtitle: "Personalizza l'aspetto del tuo mini telefono",
      language: "Impostazioni lingua",
      languageDesc: "Scegli la tua lingua preferita",
      wallpaper: "Sfondo",
      wallpaperDesc: "Carica sfondo personalizzato (Consigliato: 360x800 pixel)",
      charm: "Charm telefono",
      charmDesc: "Carica charm carino (Consigliato: 170x170 pixel, PNG con trasparenza)",
      upload: "Clicca per caricare",
      uploadHint: "o trascina i file qui",
      preview: "Anteprima",
      reset: "Ripristina predefinito",
      save: "Salva impostazioni",
      backToChat: "Torna alla chat",
      saved: "Impostazioni salvate!"
    },
    common: {
      confirm: "Conferma",
      cancel: "Annulla",
      success: "Successo",
      error: "Errore",
      loading: "Caricamento..."
    }
  },
  vi: {
    chat: {
      title: "📱 Trò chuyện Điện thoại Mini",
      placeholder: "Nhập tin nhắn...",
      send: "Gửi",
      typing: "Đang nhập...",
      clearChat: "Xóa trò chuyện",
      confirmClear: "Bạn có chắc chắn muốn xóa tất cả lịch sử trò chuyện?",
      emoji: "Biểu tượng cảm xúc"
    },
    settings: {
      title: "📱 Cài đặt Điện thoại Mini",
      subtitle: "Tùy chỉnh giao diện điện thoại mini của bạn",
      language: "Cài đặt ngôn ngữ",
      languageDesc: "Chọn ngôn ngữ ưa thích của bạn",
      wallpaper: "Hình nền",
      wallpaperDesc: "Tải lên hình nền tùy chỉnh (Khuyến nghị: 360x800 pixel)",
      charm: "Charm điện thoại",
      charmDesc: "Tải lên charm dễ thương (Khuyến nghị: 170x170 pixel, PNG trong suốt)",
      upload: "Nhấp để tải lên",
      uploadHint: "hoặc kéo và thả tệp vào đây",
      preview: "Xem trước",
      reset: "Đặt lại về mặc định",
      save: "Lưu cài đặt",
      backToChat: "Quay lại trò chuyện",
      saved: "Đã lưu cài đặt!"
    },
    common: {
      confirm: "Xác nhận",
      cancel: "Hủy",
      success: "Thành công",
      error: "Lỗi",
      loading: "Đang tải..."
    }
  },
  tr: {
    chat: {
      title: "📱 Mini Telefon Sohbet",
      placeholder: "Bir mesaj yazın...",
      send: "Gönder",
      typing: "Yazıyor...",
      clearChat: "Sohbeti temizle",
      confirmClear: "Tüm sohbet geçmişini silmek istediğinizden emin misiniz?",
      emoji: "Emoji"
    },
    settings: {
      title: "📱 Mini Telefon Ayarları",
      subtitle: "Mini telefonunuzun görünümünü özelleştirin",
      language: "Dil Ayarları",
      languageDesc: "Tercih ettiğiniz dili seçin",
      wallpaper: "Duvar kağıdı",
      wallpaperDesc: "Özel duvar kağıdı yükle (Önerilen: 360x800 piksel)",
      charm: "Telefon süsü",
      charmDesc: "Sevimli süs yükle (Önerilen: 170x170 piksel, PNG şeffaf)",
      upload: "Yüklemek için tıklayın",
      uploadHint: "veya dosyaları buraya sürükleyip bırakın",
      preview: "Önizleme",
      reset: "Varsayılana sıfırla",
      save: "Ayarları kaydet",
      backToChat: "Sohbete dön",
      saved: "Ayarlar kaydedildi!"
    },
    common: {
      confirm: "Onayla",
      cancel: "İptal",
      success: "Başarılı",
      error: "Hata",
      loading: "Yükleniyor..."
    }
  }
};

class I18n {
  private currentLanguage: Language;
  private listeners: Set<(lang: Language) => void> = new Set();

  constructor() {
    // 从 localStorage 读取保存的语言，默认为简体中文
    const savedLang = localStorage.getItem('language') as Language;
    this.currentLanguage = savedLang && this.isValidLanguage(savedLang) ? savedLang : 'zh_cn';
  }

  private isValidLanguage(lang: string): lang is Language {
    return SUPPORTED_LANGUAGES.some(l => l.code === lang);
  }

  getCurrentLanguage(): Language {
    return this.currentLanguage;
  }

  setLanguage(lang: Language): void {
    if (!this.isValidLanguage(lang)) {
      console.warn(`Invalid language: ${lang}, falling back to zh_cn`);
      lang = 'zh_cn';
    }

    this.currentLanguage = lang;
    localStorage.setItem('language', lang);

    // 更新 HTML lang 属性
    document.documentElement.lang = this.getHTMLLangCode(lang);

    // 通知所有监听器
    this.listeners.forEach(listener => listener(lang));
  }

  private getHTMLLangCode(lang: Language): string {
    const langMap: Record<Language, string> = {
      zh_cn: 'zh-CN',
      en: 'en',
      zh_tw: 'zh-TW',
      ja: 'ja',
      ar: 'ar',
      ko: 'ko',
      de: 'de',
      fr: 'fr',
      ru: 'ru',
      es: 'es',
      pt: 'pt',
      th: 'th',
      indo: 'id',
      it: 'it',
      vi: 'vi',
      tr: 'tr'
    };
    return langMap[lang] || 'zh-CN';
  }

  t(): Translations {
    return translations[this.currentLanguage];
  }

  onChange(callback: (lang: Language) => void): () => void {
    this.listeners.add(callback);
    // 返回取消订阅函数
    return () => {
      this.listeners.delete(callback);
    };
  }
}

// 导出单例
export const i18n = new I18n();

// 导出到 window 对象供非模块脚本使用
if (typeof window !== 'undefined') {
  (window as any).i18n = i18n;
  (window as any).SUPPORTED_LANGUAGES = SUPPORTED_LANGUAGES;
}
